import { useEffect, useState, useCallback } from "react";
import api, { money, formatDate, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CalendarClock, BellRing, CheckCircle2, XCircle, RotateCcw, Loader2, Send } from "lucide-react";
import { Badge } from "../components/ui/badge";

const statusTone = {
  Open: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  Renewed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  Dismissed: "bg-muted text-muted-foreground",
};

const daysTone = (d) => (d == null ? "" : d <= 15 ? "text-red-600" : d <= 45 ? "text-amber-600" : "text-muted-foreground");

export default function RenewalsPanel() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/lease-renewals").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try { await api.post(`/lease-renewals/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const remind = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/lease-renewals/${id}/remind`);
      if (data.emails_sent > 0) toast.success("Renewal reminder sent to owner & tenant.");
      else if (data.failed > 0) toast.error("Reminder failed. Is Gmail connected (Rent Reminders)?");
      else toast.info("Nothing to send for this task.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const runAll = async () => {
    if (!window.confirm("Send due renewal reminders (45 & 15-day) to owner and tenants now?")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/lease-renewals/run");
      toast.success(`Processed ${data.open_tasks} task(s): ${data.emails_sent} sent${data.failed ? `, ${data.failed} failed` : ""}.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const stagesSent = (t) => new Set((t.reminders_sent || []).map((r) => r.stage));

  return (
    <div className="space-y-4" data-testid="page-renewals">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" /> Leases ending within 45 days. Reminders auto-send to you &amp; the tenant at 45 and 15 days out.</p>
        {canWrite && (
          <button onClick={runAll} disabled={running} data-testid="run-renewals-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {running ? "Sending…" : "Send due reminders now"}
          </button>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Property</th>
                <th className="text-left font-semibold px-4 py-3">Unit</th>
                <th className="text-left font-semibold px-4 py-3">Tenant</th>
                <th className="text-left font-semibold px-4 py-3">Lease End</th>
                <th className="text-left font-semibold px-4 py-3">Days Left</th>
                <th className="text-left font-semibold px-4 py-3">Reminders</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                {canWrite && <th className="text-right font-semibold px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">No leases expiring within 45 days. 🎉</td></tr>}
              {rows.map((t) => {
                const sent = stagesSent(t);
                return (
                  <tr key={t.id} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`renewal-${t.id}`}>
                    <td className="px-4 py-3">{t.property_name}</td>
                    <td className="px-4 py-3">{t.unit || "—"}</td>
                    <td className="px-4 py-3">{t.tenant_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(t.lease_end)}</td>
                    <td className={`px-4 py-3 font-semibold whitespace-nowrap ${daysTone(t.days_left)}`}>{t.days_left != null ? `${t.days_left}d` : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Badge className={`rounded-sm text-[11px] ${sent.has(45) ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40" : "bg-muted text-muted-foreground"}`}>45d</Badge>
                        <Badge className={`rounded-sm text-[11px] ${sent.has(15) ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40" : "bg-muted text-muted-foreground"}`}>15d</Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3"><Badge className={`rounded-sm font-normal ${statusTone[t.status] || "bg-muted"}`}>{t.status}</Badge></td>
                    {canWrite && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => remind(t.id)} disabled={busy === t.id} data-testid={`renewal-remind-${t.id}`} title="Send reminder now (owner + tenant)" className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 disabled:opacity-50">
                            {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
                          </button>
                          {t.status !== "Renewed" && <button onClick={() => setStatus(t.id, "Renewed")} disabled={busy === t.id} data-testid={`renewal-renewed-${t.id}`} title="Mark renewed" className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="w-4 h-4" /></button>}
                          {t.status !== "Dismissed" ? (
                            <button onClick={() => setStatus(t.id, "Dismissed")} disabled={busy === t.id} data-testid={`renewal-dismiss-${t.id}`} title="Dismiss" className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted"><XCircle className="w-4 h-4" /></button>
                          ) : (
                            <button onClick={() => setStatus(t.id, "Open")} disabled={busy === t.id} data-testid={`renewal-reopen-${t.id}`} title="Reopen" className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted"><RotateCcw className="w-4 h-4" /></button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
